import {useState} from 'react';
import {Controller} from "react-hook-form";


import {DateRangePicker, Range} from 'react-date-range';
import {es} from 'date-fns/locale';
import {
    addDays,
    addMonths,
    differenceInCalendarDays,
    endOfDay,
    endOfMonth,
    endOfWeek,
    isSameDay,
    startOfDay,
    startOfMonth,
    startOfWeek,
} from 'date-fns';

import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css';
import {Button, Popover, Stack, TextField} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import EventIcon from '@mui/icons-material/Event';

import {dateFormatter} from "../../utils/formatters";

const defineds = {
    startOfWeek: startOfWeek(new Date()),
    endOfWeek: endOfWeek(new Date()),
    startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
    endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
    startOfToday: startOfDay(new Date()),
    endOfToday: endOfDay(new Date()),
    startOfYesterday: startOfDay(addDays(new Date(), -1)),
    endOfYesterday: endOfDay(addDays(new Date(), -1)),
    startOfMonth: startOfMonth(new Date()),
    endOfMonth: endOfMonth(new Date()),
    startOfLastMonth: startOfMonth(addMonths(new Date(), -1)),
    endOfLastMonth: endOfMonth(addMonths(new Date(), -1)),
};

const staticRangeHandler = {
    range: function () {
        return {
            startDate: new Date(),
            endDate: new Date(),
        };
    },
    isSelected: function (range: Range) {
        const definedRange = this.range();

        return (
            isSameDay(range.startDate || 0, definedRange.startDate) &&
            isSameDay(range.endDate || 0, definedRange.endDate)
        );
    },
};

export function createStaticRanges(ranges: any[]) {
    return ranges.map(range => ({...staticRangeHandler, ...range}));
}

export const staticRanges = createStaticRanges([
    {
        label: 'Hoy',
        range: () => ({
            startDate: defineds.startOfToday,
            endDate: defineds.endOfToday,
        }),
    },
    {
        label: 'Ayer',
        range: () => ({
            startDate: defineds.startOfYesterday,
            endDate: defineds.endOfYesterday,
        }),
    },

    {
        label: 'Esta semana',
        range: () => ({
            startDate: defineds.startOfWeek,
            endDate: defineds.endOfWeek,
        }),
    },
    {
        label: 'Semana pasada',
        range: () => ({
            startDate: defineds.startOfLastWeek,
            endDate: defineds.endOfLastWeek,
        }),
    },
    {
        label: 'Este Mes',
        range: () => ({
            startDate: defineds.startOfMonth,
            endDate: defineds.endOfMonth,
        }),
    },
    {
        label: 'Mes Pasado',
        range: () => ({
            startDate: defineds.startOfLastMonth,
            endDate: defineds.endOfLastMonth,
        }),
    },
]);

export const inputRanges = [
    {
        label: 'días hasta hoy',
        range(value: Range) {
            return {
                startDate: addDays(defineds.startOfToday, (Math.max(Number(value), 1) - 1) * -1),
                endDate: defineds.endOfToday,
            };
        },
        getCurrentValue(range: Range) {
            if (!isSameDay(range.endDate || 0, defineds.endOfToday)) return '-';
            if (!range.startDate) return '∞';

            return differenceInCalendarDays(defineds.endOfToday, range.startDate) + 1;
        },
    },
    {
        label: 'días desde hoy',
        range(value: Range) {
            const today = new Date();

            return {
                startDate: today,
                endDate: addDays(today, Math.max(Number(value), 1) - 1),
            };
        },
        getCurrentValue(range: Range) {
            if (!isSameDay(range.startDate || 0, defineds.startOfToday)) return '-';
            if (!range.endDate) return '∞';

            return differenceInCalendarDays(range.endDate, defineds.startOfToday) + 1;
        },
    },
];

interface ControlledDateRangePickerProps {
    control: any;
    startDateName: string,
    endDateName: string,
    label?: string,
    fullWdith?: boolean
}

export const ControlledDateRangePicker = ({
                                              control,
                                              startDateName,
                                              endDateName,
                                              label,
                                              fullWdith
                                          }: ControlledDateRangePickerProps) => {
    const theme = useTheme()

    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <Controller
                control={control}
                name={startDateName}
                render={({field: {onChange: onStartDateChange, value: valueStart}}) => (

                    <Controller
                        control={control}
                        name={endDateName}
                        render={({field: {onChange: onEndDateChange, value: valueEnd}}) => (
                            <>
                                <TextField
                                    label={label || 'Fecha Desde - Hasta'}
                                    value={`${dateFormatter.toShortDate(valueStart)} - ${dateFormatter.toShortDate(valueEnd)}`}
                                    onClick={(e) => handleClick(e)}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: <EventIcon/>
                                    }}
                                    size={'small'}
                                    fullWidth={fullWdith}
                                />
                                <Popover
                                    id={'date-picker-popover'}
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClose={handleClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                >
                                    <Stack direction={'column'}
                                           alignItems={'flex-end'}
                                    >
                                        <DateRangePicker
                                            onChange={(item) => {
                                                const {startDate, endDate} = item.selection;
                                                onStartDateChange(startDate);
                                                onEndDateChange(endDate);
                                            }}
                                            moveRangeOnFirstSelection={false}
                                            retainEndDateOnFirstSelection={false}
                                            months={2}
                                            ranges={[
                                                {
                                                    startDate: valueStart || new Date(),
                                                    endDate: valueEnd || new Date(),
                                                    key: 'selection',
                                                    color: theme.palette.primary.main
                                                },
                                            ]}
                                            direction="horizontal"
                                            preventSnapRefocus={true}
                                            calendarFocus="backwards"
                                            locale={es}
                                            staticRanges={staticRanges}
                                            color={theme.palette.primary.main}
                                            showPreview

                                            //@ts-ignore
                                            inputRanges={inputRanges}
                                        />
                                        <Stack direction={'row'}>
                                            <Button onClick={() => {
                                                onEndDateChange(undefined)
                                                onStartDateChange(undefined)
                                            }}>
                                                Limpiar
                                            </Button>

                                            <Button onClick={handleClose}
                                                    sx={{width: 1 / 4}}
                                            >
                                                Cerrar
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Popover>
                            </>
                        )}
                    />
                )}
            />
        </>
    )
}



